import { create } from "zustand"

import {
  type AssosiatedProductType,
  type ComputedProductStatus,
  type EquivalencyGroup,
  type EquivalencyGroupSortField,
  EquivalencyGroupsListDocument,
  type EquivalencyGroupsListQuery,
  type EquivalencyType,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type EquivalentProductGroupSearchFilters = SearchFilters & {
  field?: EquivalencyGroupSortField
  order?: SortingOrder
  query?: string
  equivalentTypes?: EquivalencyType[]
  productStatuses?: ComputedProductStatus[]
  associationTypes?: AssosiatedProductType[]
}

const deduplicateEquivalentProductGroups = (
  newData: EquivalencyGroup[],
  existingData: EquivalencyGroup[]
): EquivalencyGroup[] => {
  const uniqueIds = new Set(existingData.map((group) => group.equivalencyGroupId))
  return [...existingData, ...newData.filter((group) => !uniqueIds.has(group.equivalencyGroupId))]
}

export const useEquivalentProductGroupSearchStore = create(
  createSearchStore<EquivalencyGroup, EquivalentProductGroupSearchFilters, EquivalencyGroupsListQuery>({
    documentNode: EquivalencyGroupsListDocument,
    deduplicateData: deduplicateEquivalentProductGroups,
    getQueryInput: (filters: EquivalentProductGroupSearchFilters, offset: number, limit: number) => {
      const { field, order, equivalentTypes, productStatuses, associationTypes, ...otherFilters } = filters

      const typeFilter = equivalentTypes?.length === 1 ? { type: equivalentTypes[0] } : {}
      const statusFilter = productStatuses?.length ? { productStatus: productStatuses } : {}
      const associationFilter = associationTypes?.length ? { assosiatedProductType: associationTypes } : {}

      return {
        filter: {
          ...otherFilters,
          ...typeFilter,
          ...statusFilter,
          ...associationFilter,
        },
        offset,
        limit,
        sort: { field, order },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.equivalencyGroup.list.__typename,
      data:
        data.equivalencyGroup.list.__typename === "EquivalencyGroupListSuccess"
          ? (data.equivalencyGroup.list.equivalencyGroups as EquivalencyGroup[])
          : [],
      error:
        data.equivalencyGroup.list.__typename === "EquivalencyGroupFailure"
          ? data.equivalencyGroup.list.error
          : undefined,
    }),
  })
)
